import React, { useState, useEffect, useCallback, useRef } from 'react'
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { useCoachChat } from './CoachChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import { apiGet } from '~/util/api'
import { Button } from '~/components/_atoms'
import AddCoachNoteModal from './AddCoachNoteModal'
import ChatDetailsModal from './ChatDetailsModal'
import moment from 'moment'
import {
  getSectionHeaderClass,
  getEmptyStateTextClass,
  getEmptyStateIconClass,
  getUnderlinedLinkClass,
} from './coachDashboardStyles'

// CTS-9775: video chats unmount RecentChats while active, so a single fixed delay
// often races the end_chat persist. Retry within ~5s as a backup to chatHistorySaved.
const HISTORY_REFRESH_DELAYS_MS = [1000, 2500, 4500]

const RecentChats = ({ embedded = false }) => {
  const { getDarkModeClasses, chatManager } = useCoachChat()
  const { t } = useTranslation('coach_chat')
  const [recentChats, setRecentChats] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedChatForNote, setSelectedChatForNote] = useState(null)
  const [selectedChatForDetails, setSelectedChatForDetails] = useState(null)
  const refreshTimersRef = useRef([])
  const fetchRequestIdRef = useRef(0)

  const clearRefreshTimers = useCallback(() => {
    refreshTimersRef.current.forEach((timerId) => clearTimeout(timerId))
    refreshTimersRef.current = []
  }, [])

  const fetchRecentChats = useCallback(async ({ showLoading = true } = {}) => {
    const requestId = ++fetchRequestIdRef.current
    // Only show the full-page spinner for the initial load — background
    // retries must not re-enter loading, but they must still clear it if they
    // supersede an in-flight initial fetch (otherwise we get stuck on
    // "Loading recent chats..." after video remount).
    if (showLoading) setIsLoading(true)
    setError(null)
    try {
      const response = await apiGet('/coach/chat/recent_chats')
      // Ignore stale responses so an earlier mount fetch can't overwrite a
      // newer chatHistorySaved refresh (CTS-9775 video remount race).
      if (requestId !== fetchRequestIdRef.current) return

      if (response.status === 200) {
        setRecentChats(response.data.recent_chats || [])
      } else {
        throw new Error('Failed to fetch recent chats')
      }
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return
      console.error('Error fetching recent chats:', err)
      setError(err.message || 'Failed to load recent chats')
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  const scheduleHistoryRefresh = useCallback(() => {
    clearRefreshTimers()
    HISTORY_REFRESH_DELAYS_MS.forEach((delay) => {
      const timerId = setTimeout(() => {
        fetchRecentChats({ showLoading: false })
      }, delay)
      refreshTimersRef.current.push(timerId)
    })
  }, [clearRefreshTimers, fetchRecentChats])

  useEffect(() => {
    fetchRecentChats()
    // Backup retries for chats that ended just before this remount (common for video/audio).
    scheduleHistoryRefresh()
    return () => clearRefreshTimers()
  }, [fetchRecentChats, scheduleHistoryRefresh, clearRefreshTimers])

  // Listen for chat ended / history saved events and refresh when already mounted
  useEffect(() => {
    if (!chatManager) return

    // Primary path (CTS-9775): refresh as soon as end_chat persists the record.
    const handleChatHistorySaved = () => {
      clearRefreshTimers()
      fetchRecentChats({ showLoading: false })
    }

    // Backup path: chatEnded can fire before RecentChats remounts (video/audio),
    // or before the POST completes — retry within ~5s.
    const handleChatEnded = () => {
      scheduleHistoryRefresh()
    }

    chatManager.addEventListener('chatHistorySaved', handleChatHistorySaved)
    chatManager.addEventListener('chatEnded', handleChatEnded)

    return () => {
      chatManager.removeEventListener(
        'chatHistorySaved',
        handleChatHistorySaved,
      )
      chatManager.removeEventListener('chatEnded', handleChatEnded)
      clearRefreshTimers()
    }
  }, [
    chatManager,
    fetchRecentChats,
    scheduleHistoryRefresh,
    clearRefreshTimers,
  ])

  const handleNoteAdded = () => {
    // Refresh the list after note is added
    fetchRecentChats({ showLoading: false })
    setSelectedChatForNote(null)
  }

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0)
      return t('recent_chats.duration_unknown', { default: 'N/A' })
    if (minutes < 60) {
      return t('recent_chats.duration_minutes', {
        default: '%{count} min',
        count: minutes,
      })
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (mins === 0) {
      return t('recent_chats.duration_hours', {
        default: '%{count} hr',
        count: hours,
      })
    }
    return t('recent_chats.duration_hours_minutes', {
      default: '%{hours} hr %{minutes} min',
      hours,
      minutes: mins,
    })
  }

  const formatChatType = (type) => {
    if (!type) return ''
    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  const titleClasses = embedded
    ? `${getSectionHeaderClass(getDarkModeClasses)} mb-4 flex-shrink-0`
    : `text-base font-semibold ${getDarkModeClasses(
        'text-gray-800',
        'text-gray-200',
      )}`

  const containerClasses = embedded
    ? 'flex h-full min-h-0 flex-col overflow-hidden'
    : `flex h-full flex-col overflow-hidden ${getDarkModeClasses(
        'bg-gray-50',
        'bg-gray-800',
      )}`

  if (isLoading) {
    return (
      <div
        className={`flex h-full items-center justify-center ${embedded ? '' : getDarkModeClasses('bg-gray-50', 'bg-gray-800')}`}
      >
        <p
          className={`${getDarkModeClasses('text-gray-600', 'text-gray-400')}`}
        >
          {t('recent_chats.loading', 'Loading recent chats...')}
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className={`flex h-full items-center justify-center p-4 ${embedded ? '' : getDarkModeClasses('bg-gray-50', 'bg-gray-800')}`}
      >
        <p className={`${getDarkModeClasses('text-red-600', 'text-red-400')}`}>
          {t('recent_chats.error', {
            default: 'Error loading recent chats: %{error}',
            error,
          })}
        </p>
      </div>
    )
  }

  return (
    <div className={containerClasses}>
      {embedded ? (
        <h2 className={titleClasses}>
          {t('recent_chats.title', "Today's Chat History")}
        </h2>
      ) : (
        <div
          className={`flex-shrink-0 border-b px-4 py-3 ${getDarkModeClasses(
            'border-gray-200',
            'border-gray-600',
          )}`}
        >
          <h2 className={titleClasses}>
            {t('recent_chats.title', "Today's Chat History")}
          </h2>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {recentChats.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-4">
            {embedded && (
              <ChatBubbleLeftRightIcon
                className={getEmptyStateIconClass(getDarkModeClasses)}
                aria-hidden="true"
              />
            )}
            <p className={getEmptyStateTextClass(getDarkModeClasses)}>
              {t('recent_chats.no_chats', 'No recent chats today')}
            </p>
          </div>
        ) : (
          <div
            className={`divide-y ${getDarkModeClasses('divide-gray-200', 'divide-gray-700')}`}
          >
            {recentChats.map((chat) => (
              <RecentChatItem
                key={chat.id}
                chat={chat}
                formatDuration={formatDuration}
                formatChatType={formatChatType}
                onAddNote={() => setSelectedChatForNote(chat)}
                onViewDetails={() => setSelectedChatForDetails(chat)}
                getDarkModeClasses={getDarkModeClasses}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {selectedChatForNote && (
        <AddCoachNoteModal
          chat={selectedChatForNote}
          onClose={() => setSelectedChatForNote(null)}
          onSuccess={handleNoteAdded}
        />
      )}

      {selectedChatForDetails && (
        <ChatDetailsModal
          chat={selectedChatForDetails}
          onClose={() => setSelectedChatForDetails(null)}
          getDarkModeClasses={getDarkModeClasses}
          t={t}
        />
      )}
    </div>
  )
}

const RecentChatItem = ({
  chat,
  formatDuration,
  formatChatType,
  onAddNote,
  onViewDetails,
  getDarkModeClasses,
  t,
}) => {
  const lastMessageStyle =
    chat.last_message === 'Chat request missed by coach'
      ? 'font-bold text-red-600 dark:text-red-400'
      : ''

  const candidateNameClasses = `font-semibold ${getDarkModeClasses(
    'text-gray-800',
    'text-gray-200',
  )}`
  const candidateLinkClasses = `font-semibold ${getUnderlinedLinkClass(
    getDarkModeClasses,
  )}`

  return (
    <div
      className={`p-4 hover:${getDarkModeClasses('bg-gray-100', 'bg-gray-700')}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className={candidateNameClasses}>
          {chat.candidate_id ? (
            <a
              href={`/coach/candidates/${chat.candidate_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={candidateLinkClasses}
            >
              {chat.candidate_name}
            </a>
          ) : (
            chat.candidate_name
          )}
        </div>
        <div
          className={`text-sm ${getDarkModeClasses('text-gray-500', 'text-gray-400')}`}
        >
          {moment(chat.ended_at).format('h:mm A')}
        </div>
      </div>

      <div className="mb-2 flex gap-4 text-sm">
        <div
          className={`${getDarkModeClasses('text-gray-600', 'text-gray-400')}`}
        >
          <span className="font-medium">
            {t('recent_chats.duration_label', 'Duration:')}
          </span>{' '}
          {formatDuration(chat.duration_minutes)}
        </div>
        <div
          className={`${getDarkModeClasses('text-gray-600', 'text-gray-400')}`}
        >
          <span className="font-medium">
            {t('recent_chats.type_label', 'Type:')}
          </span>{' '}
          {formatChatType(chat.chat_type)}
        </div>
      </div>

      {chat.last_message && (
        <div
          className={`mb-3 truncate text-sm ${lastMessageStyle} ${getDarkModeClasses(
            'text-gray-700',
            'text-gray-300',
          )}`}
          title={chat.last_message}
        >
          {chat.last_message_attachment_url ? (
            <a
              href={chat.last_message_attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-90"
              onClick={(e) => e.stopPropagation()}
            >
              {chat.last_message}
            </a>
          ) : (
            chat.last_message
          )}
        </div>
      )}

      <div className="flex gap-2">
        {!chat.has_coach_note && (
          <Button
            variant="outline"
            size="sm"
            onClick={onAddNote}
            className="flex-1"
          >
            {t('recent_chats.add_note', 'Add Coach Note')}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onViewDetails}
          className="flex-1"
        >
          {t('recent_chats.view_details', 'View Details')}
        </Button>
      </div>
    </div>
  )
}

export default RecentChats
